import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { AlertTriangle, BellRing, Volume2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { requestNotificationPermission } from '../hooks/useDesktopNotifications';
import { useNotificationPrefs } from '../store';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NotificationSettingsModal({ open, onClose }: Props) {
  useEscapeClose(open, onClose);
  const prefs = useNotificationPrefs();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );

  useEffect(() => {
    if (!open) return;
    if (typeof Notification === 'undefined') return;
    setPermission(Notification.permission);
  }, [open]);

  if (!open) return null;

  const askPermission = async (): Promise<void> => {
    const p = await requestNotificationPermission();
    setPermission(p);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[520px] max-w-[92vw] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellRing size={18} className="text-brand-500" />
            <h2 className="font-bold text-[16px] text-n-900">Configuração das notificações</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {permission === 'denied' && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-warning-50 px-3 py-2.5 text-[12px] text-warning-700">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>Notificações bloqueadas no navegador.</strong> Vá no cadeado da URL e habilite
              "Notificações" pra esse site, depois recarregue a página.
            </div>
          </div>
        )}

        {permission === 'default' && (
          <div className="mt-4 flex items-center justify-between gap-2 rounded-md bg-brand-50 px-3 py-2.5 text-[12px] text-brand-700">
            <div className="flex items-start gap-2">
              <BellRing size={14} className="mt-0.5 flex-shrink-0" />
              <span>Permita notificações pra receber alertas mesmo com aba em segundo plano.</span>
            </div>
            <button
              type="button"
              onClick={askPermission}
              className="flex-shrink-0 rounded-md bg-brand-500 px-2.5 py-1 font-semibold text-[11px] text-white hover:bg-brand-600"
            >
              Permitir
            </button>
          </div>
        )}

        {permission === 'unsupported' && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-n-100 px-3 py-2.5 text-[12px] text-n-600">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>Este navegador não suporta notificações desktop.</span>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-1">
          <ToggleRow
            label="Novos tickets no pendente"
            checked={prefs.newTicketPending}
            onChange={(v) => prefs.setPref('newTicketPending', v)}
          />
          <ToggleRow
            label="Novos tickets no ativo"
            checked={prefs.newTicketActive}
            onChange={(v) => prefs.setPref('newTicketActive', v)}
          />
          <ToggleRow
            label="Novas mensagens no ativo"
            checked={prefs.newMessageActive}
            onChange={(v) => prefs.setPref('newMessageActive', v)}
          />
          <ToggleRow
            label="Novas mensagens no pendente"
            checked={prefs.newMessagePending}
            onChange={(v) => prefs.setPref('newMessagePending', v)}
          />

          <div className="mt-3 border-n-200 border-t pt-3">
            <ToggleRow
              icon={<Volume2 size={14} className="text-n-500" />}
              label="Som de alerta"
              checked={prefs.soundEnabled}
              onChange={(v) => prefs.setPref('soundEnabled', v)}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white hover:bg-brand-600"
          >
            Concluído
          </button>
        </div>
      </div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  icon?: React.ReactNode;
}

function ToggleRow({ label, checked, onChange, icon }: ToggleRowProps) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-2.5 transition hover:bg-n-50">
      <span className="flex items-center gap-2 text-[13px] text-n-800">
        {icon}
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-[22px] w-[40px] flex-shrink-0 items-center rounded-pill transition ${
          checked ? 'bg-brand-500' : 'bg-n-200'
        }`}
      >
        <span
          className={`absolute h-[18px] w-[18px] rounded-pill bg-n-0 shadow-sm transition ${
            checked ? 'translate-x-[20px]' : 'translate-x-[2px]'
          }`}
        />
      </button>
    </label>
  );
}
